import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Building2, Palette, UserRound, ArrowRight, ArrowLeft, Check, Loader2, Upload } from "lucide-react";
import { toast } from "sonner";
import { api, setToken } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const STEPS = [
  { key: "company", label: "Company", icon: Building2 },
  { key: "branding", label: "Branding", icon: Palette },
  { key: "finish", label: "Finish", icon: UserRound },
];

export default function SetupWizard() {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [status, setStatus] = useState(null);
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    company_name: "", company_website: "", company_description: "",
    brand_primary: "#2899FF", brand_dark: "#1F2937",
    admin_name: "", admin_email: "", admin_password: "",
  });
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);

  const isAdmin = user?.role === "admin";
  const needsAdminAccount = status && !status.has_admin;

  useEffect(() => {
    api.get("/setup/status").then((r) => setStatus(r.data)).catch(() => setStatus({ needs_setup: true, has_admin: true }));
  }, []);

  useEffect(() => {
    if (loading || !status) return;
    if (!status.needs_setup) { navigate("/", { replace: true }); return; }
    if (status.has_admin && !user) { navigate("/login", { replace: true }); return; }
    if (status.has_admin && user && !isAdmin) { navigate("/", { replace: true }); }
  }, [loading, status, user, isAdmin, navigate]);

  useEffect(() => {
    if (!isAdmin) return;
    api.get("/admin/settings").then((r) => {
      const s = r.data || {};
      setForm((f) => ({
        ...f,
        company_name: s.company_name || f.company_name,
        company_website: s.company_website || f.company_website,
        company_description: s.company_description || f.company_description,
        brand_primary: s.brand_primary || f.brand_primary,
        brand_dark: s.brand_dark || f.brand_dark,
      }));
    }).catch(() => {});
  }, [isAdmin]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const pickLogo = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
  };

  const canNext = step === 0 ? form.company_name.trim().length > 1 : true;
  const canFinish = needsAdminAccount
    ? form.admin_email.includes("@") && form.admin_password.length >= 8
    : true;

  const finish = async () => {
    setSaving(true);
    try {
      const payload = {
        company_name: form.company_name.trim(),
        company_website: form.company_website.trim(),
        company_description: form.company_description.trim(),
        brand_primary: form.brand_primary,
        brand_dark: form.brand_dark,
        footer_accent_color: form.brand_primary,
      };
      if (needsAdminAccount) {
        payload.admin_name = form.admin_name.trim();
        payload.admin_email = form.admin_email.trim();
        payload.admin_password = form.admin_password;
      }
      const { data } = await api.post("/setup", payload);
      if (data.access_token) setToken(data.access_token);
      if (logoFile) {
        const fd = new FormData();
        fd.append("file", logoFile);
        await api.post("/admin/settings/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success("Setup complete — welcome!");
      window.location.href = "/";
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Setup failed — please try again");
      setSaving(false);
    }
  };

  if (loading || !status) {
    return <div className="min-h-screen flex items-center justify-center bg-[#14171A] text-gray-400 text-sm">Loading…</div>;
  }

  return (
    <div className="min-h-screen bg-[#14171A] text-white flex items-center justify-center px-4 py-10" data-testid="setup-wizard">
      <div className="w-full max-w-xl">
        <div className="mb-8">
          <div className="text-xs tracking-[0.25em] font-semibold" style={{ color: form.brand_primary }}>
            HTM PROJECT HUB
          </div>
          <h1 className="text-3xl sm:text-4xl font-extrabold mt-2">Let's set up your portal</h1>
          <p className="text-gray-400 mt-2 text-sm">
            This makes the whole portal — and every AI-written word — about your company.
          </p>
        </div>

        {/* Step indicator */}
        <div className="flex items-center gap-2 mb-8" data-testid="setup-steps">
          {STEPS.map((s, i) => {
            const Icon = s.icon;
            const active = i === step;
            const done = i < step;
            return (
              <React.Fragment key={s.key}>
                <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold border ${
                  active ? "border-white/40 bg-white/10 text-white"
                  : done ? "border-transparent text-gray-300"
                  : "border-white/10 text-gray-500"}`}>
                  {done ? <Check className="h-3.5 w-3.5" style={{ color: form.brand_primary }} /> : <Icon className="h-3.5 w-3.5" />}
                  {s.label}
                </div>
                {i < STEPS.length - 1 && <div className="flex-1 h-px bg-white/10" />}
              </React.Fragment>
            );
          })}
        </div>

        <div className="bg-white/5 border border-white/10 rounded-2xl p-6 sm:p-8 backdrop-blur">
          {step === 0 && (
            <div className="space-y-5">
              <div>
                <label className="block text-xs font-semibold tracking-wide text-gray-300 mb-1.5">COMPANY NAME *</label>
                <input value={form.company_name} onChange={set("company_name")}
                  placeholder="e.g. Highway Traffic Management Ltd"
                  className="w-full rounded-lg bg-white/10 border border-white/15 px-4 py-3 text-sm outline-none focus:border-white/40"
                  data-testid="setup-company-name" />
              </div>
              <div>
                <label className="block text-xs font-semibold tracking-wide text-gray-300 mb-1.5">WEBSITE</label>
                <input value={form.company_website} onChange={set("company_website")}
                  placeholder="https://yourcompany.co.uk"
                  className="w-full rounded-lg bg-white/10 border border-white/15 px-4 py-3 text-sm outline-none focus:border-white/40"
                  data-testid="setup-company-website" />
              </div>
              <div>
                <label className="block text-xs font-semibold tracking-wide text-gray-300 mb-1.5">WHAT DOES THE COMPANY DO?</label>
                <textarea value={form.company_description} onChange={set("company_description")} rows={4}
                  placeholder="e.g. We provide temporary traffic management solutions across the highways industry…"
                  className="w-full rounded-lg bg-white/10 border border-white/15 px-4 py-3 text-sm outline-none focus:border-white/40 resize-none"
                  data-testid="setup-company-description" />
                <p className="text-[11px] text-gray-500 mt-1.5">
                  The AI uses this to write in your industry's language — the more specific, the better.
                </p>
              </div>
            </div>
          )}

          {step === 1 && (
            <div className="space-y-6">
              <div>
                <label className="block text-xs font-semibold tracking-wide text-gray-300 mb-2">COMPANY LOGO</label>
                <label className="flex items-center gap-4 rounded-xl border border-dashed border-white/20 bg-white/5 px-4 py-5 cursor-pointer hover:border-white/40 transition-colors" data-testid="setup-logo-drop">
                  {logoPreview ? (
                    <img src={logoPreview} alt="Logo preview" className="h-12 w-auto object-contain bg-white rounded p-1" />
                  ) : (
                    <div className="h-12 w-12 rounded-lg bg-white/10 flex items-center justify-center">
                      <Upload className="h-5 w-5 text-gray-400" />
                    </div>
                  )}
                  <div>
                    <div className="text-sm font-medium">{logoFile ? logoFile.name : "Upload your logo"}</div>
                    <div className="text-[11px] text-gray-500">PNG, JPG, WEBP or SVG · max 4 MB · shown in the header, PDFs and emails</div>
                  </div>
                  <input type="file" accept=".png,.jpg,.jpeg,.webp,.svg" className="hidden" onChange={pickLogo} data-testid="setup-logo-input" />
                </label>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold tracking-wide text-gray-300 mb-2">PRIMARY / ACCENT</label>
                  <div className="flex items-center gap-2">
                    <input type="color" value={form.brand_primary} onChange={set("brand_primary")}
                      className="h-10 w-12 rounded cursor-pointer bg-transparent border border-white/15" data-testid="setup-color-primary" />
                    <input value={form.brand_primary} onChange={set("brand_primary")}
                      className="flex-1 rounded-lg bg-white/10 border border-white/15 px-3 py-2 text-sm outline-none" />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-semibold tracking-wide text-gray-300 mb-2">DARK / HEADINGS</label>
                  <div className="flex items-center gap-2">
                    <input type="color" value={form.brand_dark} onChange={set("brand_dark")}
                      className="h-10 w-12 rounded cursor-pointer bg-transparent border border-white/15" data-testid="setup-color-dark" />
                    <input value={form.brand_dark} onChange={set("brand_dark")}
                      className="flex-1 rounded-lg bg-white/10 border border-white/15 px-3 py-2 text-sm outline-none" />
                  </div>
                </div>
              </div>
              <div className="rounded-xl overflow-hidden border border-white/10">
                <div className="px-4 py-3 text-xs text-gray-400 bg-white/5">Button preview</div>
                <div className="p-4 bg-white flex items-center gap-3">
                  <span className="px-4 py-2 rounded-lg text-sm font-semibold" style={{ background: form.brand_primary, color: "#fff" }}>
                    Start Submission
                  </span>
                  <span className="px-4 py-2 rounded-lg text-sm font-semibold text-white" style={{ background: form.brand_dark }}>
                    Secondary
                  </span>
                </div>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-5">
              {needsAdminAccount ? (
                <>
                  <p className="text-sm text-gray-300">Create the first admin account for this portal.</p>
                  <input value={form.admin_name} onChange={set("admin_name")} placeholder="Your name"
                    className="w-full rounded-lg bg-white/10 border border-white/15 px-4 py-3 text-sm outline-none focus:border-white/40" data-testid="setup-admin-name" />
                  <input value={form.admin_email} onChange={set("admin_email")} placeholder="you@company.com" type="email"
                    className="w-full rounded-lg bg-white/10 border border-white/15 px-4 py-3 text-sm outline-none focus:border-white/40" data-testid="setup-admin-email" />
                  <input value={form.admin_password} onChange={set("admin_password")} placeholder="Password (min 8 characters)" type="password"
                    className="w-full rounded-lg bg-white/10 border border-white/15 px-4 py-3 text-sm outline-none focus:border-white/40" data-testid="setup-admin-password" />
                </>
              ) : (
                <div className="space-y-3" data-testid="setup-summary">
                  <p className="text-sm text-gray-300">All set. Here's what your portal will use:</p>
                  <div className="rounded-xl bg-white/5 border border-white/10 divide-y divide-white/5 text-sm">
                    <div className="px-4 py-3 flex justify-between gap-4"><span className="text-gray-500">Company</span><span className="font-medium text-right">{form.company_name || "—"}</span></div>
                    <div className="px-4 py-3 flex justify-between gap-4"><span className="text-gray-500">Website</span><span className="font-medium text-right">{form.company_website || "—"}</span></div>
                    <div className="px-4 py-3 flex justify-between gap-4"><span className="text-gray-500">Logo</span><span className="font-medium">{logoFile ? logoFile.name : "Not uploaded (add later in Settings)"}</span></div>
                    <div className="px-4 py-3 flex justify-between gap-4"><span className="text-gray-500">Colours</span>
                      <span className="flex items-center gap-2">
                        <span className="h-4 w-4 rounded" style={{ background: form.brand_primary }} />
                        <span className="h-4 w-4 rounded" style={{ background: form.brand_dark }} />
                      </span>
                    </div>
                  </div>
                  <p className="text-[11px] text-gray-500">Every AI enhancement, case study, article and social post will now be written for and about {form.company_name || "your company"}. You can change any of this later in Admin → Settings.</p>
                </div>
              )}
            </div>
          )}

          <div className="flex items-center justify-between mt-8">
            {step > 0 ? (
              <button onClick={() => setStep(step - 1)} disabled={saving}
                className="inline-flex items-center gap-2 text-sm text-gray-400 hover:text-white transition-colors" data-testid="setup-back-btn">
                <ArrowLeft className="h-4 w-4" /> Back
              </button>
            ) : <span />}
            {step < 2 ? (
              <button onClick={() => setStep(step + 1)} disabled={!canNext}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold disabled:opacity-40 transition-opacity"
                style={{ background: form.brand_primary, color: "#fff" }} data-testid="setup-next-btn">
                Continue <ArrowRight className="h-4 w-4" />
              </button>
            ) : (
              <button onClick={finish} disabled={!canFinish || saving}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold disabled:opacity-40 transition-opacity"
                style={{ background: form.brand_primary, color: "#fff" }} data-testid="setup-finish-btn">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                Finish setup
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
